import { useCallback } from 'react'

import type { one_to_one } from '@fireworkhq/proto-contract'
import { useVisitorChannelContext } from '@liveHelper/context/VisitorChannelContext'
import {
  actionsSelector,
  conversationSelector,
  paramsSelector,
  useStore,
  useStoreApi,
} from '@liveHelper/models/store'
import { TRANSFERRING_MESSAGE_REF } from '@oneToOne/shared/components/chat/constants'
import { useSocketContext } from '@oneToOne/shared/contexts/SocketContext'
import type { PushEvent, ServerEvent } from '@oneToOne/shared/ws/message'

import { useContextTranslation } from '@core/i18n'
import { useChannel } from '@core/phoenix'
import { getGuestId } from '@core/tracking/guestId'

import { useHandleServerMessages } from './useHandleServerMessages'

const EMPTY_PAYLOAD = {}

/**
 * Hook to create and join message channel.
 * @returns The channel instance
 * @private Private to MessageChannelContext
 */
export function useMessageChannel() {
  const { t } = useContextTranslation()

  const { socket } = useSocketContext()
  const visitorChannel = useVisitorChannelContext()
  const { chat_channel_id: chatChannelId } = useStore(paramsSelector)
  const guestId = getGuestId()
  const topic = chatChannelId ? `oto:message:${chatChannelId}:${guestId}` : ''
  const storeApi = useStoreApi()
  const actions = actionsSelector(storeApi.getState())
  const conversation = useStore(conversationSelector)
  const handleMessages = useHandleServerMessages()

  const onJoinSuccess = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization -- advisory only (React Compiler not enabled); memoization intentionally kept
    (response: one_to_one.message.ChannelJoinSuccessResponse) => {
      actions.setChatMessages(
        response.messages ?? [],
        'visitor',
        response?.next_cursor ?? null,
      )
      if (
        conversation?.id &&
        conversation?.intent === 'CONVERSATION_INTENT_POST_SALES' &&
        !conversation?.assistance_requested
      ) {
        actions.addChatActions(conversation.id)
      }

      if (conversation?.transfer_details) {
        const message: one_to_one.shared.Message = {
          client_msg_ref: TRANSFERRING_MESSAGE_REF,
          created_at: new Date().toISOString(),
          system_message: {
            content: t('oneToOne:Transferring...'),
            type: 'SYSTEM_MESSAGE_TYPE_INFO',
          },
        }
        actions.addChatMessages([message], 'visitor')
      }
    },
    [
      t,
      actions,
      conversation?.assistance_requested,
      conversation?.id,
      conversation?.intent,
      conversation?.transfer_details,
    ],
  )

  return useChannel<PushEvent, ServerEvent>({
    socket,
    topic,
    payload: EMPTY_PAYLOAD,
    shouldJoin: !!(topic && visitorChannel),
    onJoinSuccess,
    handleMessages,
  })
}
